import React, { useMemo, useState } from "react";

import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Download,
  Package,
  Pencil,
  PlusIcon,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import * as XLSX from "xlsx";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { useProducts } from "~/hooks/useProducts";
import { ProductDialog } from "~/pages/SuperAdminDashboard/ProductDialog";
import { ProductImportDialog } from "~/pages/SuperAdminDashboard/ProductImportDialog";
import { Product } from "~/types/product";
import { formatCurrency } from "~/utils/helpers";

type SortKey =
  | "barcode"
  | "short_name"
  | "group_description"
  | "net_price"
  | "suggested_price"
  | "stock"
  | "discount_percent"
  | "weight_kg"
  | "volume_cm3"
  | "visible_to_sellers";

type SortDirection = "asc" | "desc";

export const ProductsTab: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>("short_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const {
    products,
    loading,
    saving,
    deleteProduct,
    saveProduct,
    importProducts,
  } = useProducts();

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const filteredAndSortedProducts = useMemo(() => {
    let result = products;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.short_name?.toLowerCase().includes(q) ||
          p.long_name?.toLowerCase().includes(q) ||
          p.description?.toLowerCase().includes(q) ||
          p.barcode?.toLowerCase().includes(q) ||
          p.sku?.toLowerCase().includes(q) ||
          p.group_description?.toLowerCase().includes(q) ||
          p.subgroup_description?.toLowerCase().includes(q)
      );
    }

    if (sortKey) {
      result = [...result].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp =
          typeof aVal === "string"
            ? aVal.localeCompare(bVal as string)
            : (aVal as number) - (bVal as number);
        return sortDirection === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [products, searchQuery, sortKey, sortDirection]);

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setIsDialogOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    console.log("Product being edited:", product);
    console.log("Product SKU:", product.sku);
    setSelectedProduct(product);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
  };

  const handleConfirmDelete = async () => {
    if (productToDelete?.id) {
      await deleteProduct(productToDelete.id);
    }
    setProductToDelete(null);
  };

  const handleSaveProduct = async (product: Product) => {
    const isNew = !product.id;
    await saveProduct(product, isNew);
    setIsDialogOpen(false);
  };

  const handleImportProducts = async (
    products: Partial<Product>[],
    updateMode: boolean = false
  ) => {
    await importProducts(products, updateMode);
    setIsImportDialogOpen(false);
  };

  const exportToExcel = () => {
    try {
      // Prepare data for Excel export
      const excelData = products.map((product) => ({
        "Código Barras": product.barcode || "",
        "Nombre Corto": product.short_name,
        "Nombre Largo": product.long_name || "",
        Descripción: product.description || "",
        SKU: product.sku || "",
        "Código de Grupo": product.group_code || "",
        "Descripción de Grupo": product.group_description || "",
        "Código de Subgrupo": product.subgroup_code || "",
        "Descripción de Subgrupo": product.subgroup_description || "",
        "Precio Neto": product.net_price || 0,
        "Precio Sugerido": product.suggested_price || 0,
        "Precio por Kilos": product.price_in_kilos || 0,
        Stock: product.stock,
        "Descuento (%)": product.discount_percent || 0,
        "Peso (kg)": product.weight_kg || 0,
        "Volumen (cm³)": product.volume_cm3 || 0,
        "Código de Unidad": product.unit_code || "",
        "Unidad Base de Medida": product.base_measurement_unit || "",
        "URL de Imagen": product.product_image_url || "",
        "Visible para Vendedores":
          product.visible_to_sellers !== false ? "Sí" : "No",
        "Fecha de Creación": product.created_at,
        "Fecha de Actualización": product.updated_at,
        "Fecha de Eliminación": product.deleted_at || "No eliminado",
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 15 }, // Código Barras
        { wch: 20 }, // Nombre Corto
        { wch: 30 }, // Nombre Largo
        { wch: 40 }, // Descripción
        { wch: 15 }, // SKU
        { wch: 15 }, // Código de Grupo
        { wch: 25 }, // Descripción de Grupo
        { wch: 15 }, // Código de Subgrupo
        { wch: 25 }, // Descripción de Subgrupo
        { wch: 15 }, // Precio Neto
        { wch: 15 }, // Precio Sugerido
        { wch: 15 }, // Precio por Kilos
        { wch: 10 }, // Stock
        { wch: 12 }, // Descuento (%)
        { wch: 12 }, // Peso (kg)
        { wch: 15 }, // Volumen (cm³)
        { wch: 15 }, // Código de Unidad
        { wch: 20 }, // Unidad Base de Medida
        { wch: 30 }, // URL de Imagen
        { wch: 20 }, // Visible para Vendedores
        { wch: 20 }, // Fecha de Creación
        { wch: 20 }, // Fecha de Actualización
        { wch: 20 }, // Fecha de Eliminación
      ];
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Productos");

      // Generate filename with current date
      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `productos_${currentDate}.xlsx`;

      // Write and download file
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Excel Exportado",
        description: `Se ha descargado el archivo ${filename} con ${products.length} productos.`,
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el archivo Excel.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex max-w-[90vw] flex-col gap-4 sm:px-10 md:max-w-[70vw]">
      <div className="flex flex-col justify-between gap-y-4 sm:flex-row sm:items-center">
        <h2 className="text-2xl font-bold">Inventario de Productos</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={exportToExcel}
            disabled={products.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Importar Productos
          </Button>
          <Button onClick={handleAddProduct}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, código de barras, SKU, grupo..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4">
              <TableSkeleton rows={8} columns={11} />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {(
                    [
                      ["barcode", "Código Barras"],
                      ["short_name", "Producto"],
                      ["group_description", "Grupo"],
                      ["net_price", "Precio Neto"],
                      ["suggested_price", "Precio Sugerido"],
                      ["stock", "Stock"],
                      ["discount_percent", "Dcto. %"],
                      ["weight_kg", "Peso (kg)"],
                      ["volume_cm3", "Volumen (cm³)"],
                      ["visible_to_sellers", "Visible"],
                    ] as [SortKey, string][]
                  ).map(([key, label]) => (
                    <TableHead key={key}>
                      <button
                        className="flex items-center gap-1 hover:text-foreground"
                        onClick={() => handleSort(key)}
                      >
                        {label}
                        {sortKey === key ? (
                          sortDirection === "asc" ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
                        )}
                      </button>
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedProducts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {searchQuery.trim()
                        ? "No se encontraron productos que coincidan con la búsqueda."
                        : 'No hay productos disponibles. Haga clic en "Nuevo Producto" para agregar uno.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedProducts.map((product, index) => (
                    <TableRow
                      key={product.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell>{product.barcode || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {product.product_image_url ? (
                            <img
                              src={product.product_image_url}
                              alt={product.short_name}
                              className="h-10 w-10 rounded border object-contain"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded border bg-gray-100">
                              <Package className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {product.short_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {product.description || product.long_name || ""}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {product.group_description || "-"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {product.subgroup_description || ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        {product.net_price
                          ? formatCurrency(product.net_price)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {product.suggested_price
                          ? formatCurrency(product.suggested_price)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            product.stock > 50
                              ? "bg-green-100 text-green-800"
                              : product.stock > 10
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {product.stock}
                        </span>
                      </TableCell>
                      <TableCell>
                        {product.discount_percent
                          ? `${product.discount_percent}%`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {product.weight_kg
                          ? `${product.weight_kg.toFixed(3)} kg`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {product.volume_cm3
                          ? `${product.volume_cm3.toFixed(3)} cm³`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            product.visible_to_sellers !== false
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {product.visible_to_sellers !== false
                            ? "Visible"
                            : "Oculto"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(product)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ProductDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        product={selectedProduct}
        onSave={handleSaveProduct}
        saving={saving}
      />

      <ProductImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImport={handleImportProducts}
        products={products}
      />

      <AlertDialog
        open={!!productToDelete}
        onOpenChange={(open) => !open && setProductToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Está seguro que desea eliminar este producto?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no eliminará permanentemente el producto, pero lo
              ocultará de las vistas. Puede restaurarlo más tarde desde la
              interfaz de administración.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsTab;
